import { useMemo } from 'react';

import { Card, Flex, Icons, Table, theme, Typography } from '@contiple/antd-ds';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ORG_INFO_ROUTE } from '@/constants';
import { ORG_FIELD } from '@/constants/organization';
import { useAppStore } from '@/store';
import type { DashboardOrganization } from '@/types/dashboard';
import type { Organization } from '@/types/organization';

const { Text } = Typography;

const TOP_N = 5;

interface OrganizationTableProps {
  organizations: DashboardOrganization[];
}

export default function OrganizationTable({
  organizations,
}: OrganizationTableProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { setSelectedOrg, setSecondMenuTitle, setFirstSelectedMenu } =
    useAppStore();

  // 백엔드가 최신 생성순(위→아래)으로 내려주므로 그대로 노출 (QA-309)
  const topOrganizations = useMemo(
    () => organizations.slice(0, TOP_N),
    [organizations]
  );

  const handleOrgClick = (org: DashboardOrganization) => {
    setSelectedOrg({
      [ORG_FIELD.ID]: org.organizationId,
      [ORG_FIELD.NAME]: org.organizationName,
    } as Organization);
    setFirstSelectedMenu('/organization');
    setSecondMenuTitle({ '/organization': org.organizationName });
    navigate(ORG_INFO_ROUTE);
  };

  const cellProps = () => ({ style: { padding: '12px 16px' } });

  const columns: ColumnsType<DashboardOrganization> = [
    {
      title: <Text type="secondary">{t('dashboard.orgName')}</Text>,
      dataIndex: 'organizationName',
      key: 'organizationName',
      onHeaderCell: cellProps,
      onCell: cellProps,
      render: (name: string, record: DashboardOrganization) => (
        <Text
          style={{ cursor: 'pointer' }}
          onClick={() => handleOrgClick(record)}>
          {name}
        </Text>
      ),
    },
    {
      title: <Text type="secondary">{t('dashboard.serviceCount')}</Text>,
      dataIndex: 'serviceCount',
      key: 'serviceCount',
      align: 'center' as const,
      onHeaderCell: cellProps,
      onCell: cellProps,
      className: 'tabular-nums',
    },
    {
      title: <Text type="secondary">{t('dashboard.memberCount')}</Text>,
      dataIndex: 'memberCount',
      key: 'memberCount',
      align: 'center' as const,
      onHeaderCell: cellProps,
      onCell: cellProps,
      className: 'tabular-nums',
    },
  ];

  return (
    <Card
      title={t('dashboard.myOrganization')}
      extra={
        <Icons.ChevronRight
          size={16}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setFirstSelectedMenu('/organization');
            navigate('/organization/index');
          }}
        />
      }
      style={{
        flex: 1,
        borderRadius: 12,
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
      }}
      styles={{
        header: {
          padding: '20px 20px 16px',
          minHeight: 0,
          borderBottom: 'none',
        },
        body: { padding: '12px 20px 20px' },
      }}>
      {organizations.length > 0 ? (
        <Table
          columns={columns}
          dataSource={topOrganizations}
          rowKey="organizationId"
          pagination={false}
          size="small"
          style={{
            border: `1px solid ${token.colorBorder}`,
            borderRadius: 12,
            overflow: 'hidden',
          }}
        />
      ) : (
        <Flex justify="center" align="center" style={{ height: 300 }}>
          <Text type="secondary">{t('dashboard.emptyOrganization')}</Text>
        </Flex>
      )}
    </Card>
  );
}
